import { Account } from '../../../assets/constant/accountManager.type';
import { ConnectedWallet } from '../../../assets/constant/kit.type';
import React, { useCallback, useMemo, useState } from 'react';
import { logic } from '../../../../logic';
import useLoading from '../../../../hooks/useLoading';
import RadioButton from '../../../../legacy-common/atom/button/RadioButton';
import OwnerBadge from './OwnerBadge';
import CreateAccountModal from './manager/account/CreateAccountModal';
import PageLoading from '../../../components/_atom/loading/PageLoading';
import EmptyDataTemplate from '../../dashboard/components/EmptyDataTemplate';
import BasicTable from '../../../components/_atom/table/BasicTable';
import { PagingBar } from '../../../components/_atom/paging/PagingBar';
import BasicButton from '../../../components/_atom/button/BasicButton';
import { ButtonTheme } from '../../../components/_atom/button/StyledButton';
import { SELECT_ACCOUNT_STEP } from './ConnectWalletModal';

const ACCOUNTS_LIMIT = 5;

/**
 * SelectAccount component
 * @description
 * 	SelectAccount 컴포넌트는 GetAddressOfAccountModal, ConnectWalletModal 컴포넌트에서 사용되는 컴포넌트로
 * 	계정 목록을 Table 형태로 보여주고 계정을 선택하는 컴포넌트입니다.
 * 	@param {Function} onClickSelect 계정 선택 시 실행되는 함수
 *  @param {Function} onClosed 모달 닫힐 때 실행되는 함수
 *  @param {Function} setStep 현재 스텝을 설정하는 함수
 *  @param {string} kitOwnerAddress 키트 소유자 주소
 *  @param {number} kitId 키트 아이디
 *  @param {number} categoryIdentifier 카테고리 아이디
 *  @param {number} chainId 체인 아이디
 */
const SelectAccount = ({
  onClickSelect,
  onClosed,
  setStep,
  kitOwnerAddress,
  kitId,
  categoryIdentifier,
  chainId,
}: {
  onClickSelect: (account: Account) => void;
  onClosed: (connectedWallet?: ConnectedWallet) => void;
  setStep?: (step: SELECT_ACCOUNT_STEP) => void;
  kitOwnerAddress?: string;
  kitId?: number;
  categoryIdentifier?: number;
  chainId?: number;
}) => {
  const [selectedAccount, setSelectedAccount] = useState<Account>();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [maxAccountsNum, setMaxAccountsNum] = useState(0);
  const getAccounts = async (option?: { lastId?: string; offset?: number; order?: 'ASC' | 'DESC' }) => {
    const { result, error } = await logic.accountManager.getAccountManagerListWithBalance({
      limit: ACCOUNTS_LIMIT,
      kitId,
      // categoryIdentifier,
      microChainId: chainId,
      ...option,
    });
    if (error) {
      return;
    }
    const { rows: accounts, count: maxAccountsNum } = result;
    setAccounts(accounts);
    setMaxAccountsNum(maxAccountsNum);
  };
  const [isOpenRegisterAccountModal, setIsOpenRegisterAccountModal] = useState(false);
  const pageNum = Number(new URLSearchParams(window.location.search).get('select-account')) || 1;
  const maxPageNum = maxAccountsNum % ACCOUNTS_LIMIT === 0 ? maxAccountsNum / ACCOUNTS_LIMIT : Math.floor(maxAccountsNum / ACCOUNTS_LIMIT) + 1;
  const [refresh, setRefresh] = useState(false);
  const { loading } = useLoading({
    dependency: [pageNum, refresh],
    synchronousFunction: async () => {
      await getAccounts({ offset: (pageNum - 1) * ACCOUNTS_LIMIT });
    },
  });

  const ACCOUNT_COLGROUP = useMemo(() => {
    if (!chainId) {
      return [1, 5.833, 5.833, 5.833];
    }
    if (!kitId) {
      return [1, 4.375, 4.375, 4.375, 4.375];
    }
    return [1, 3.5, 3.5, 3.5, 3.5, 3.5];
  }, [kitId, chainId]);
  const ACCOUNT_HEADERS = useMemo(() => {
    if (!chainId) {
      return [
        { title: '', accessor: 'select' },
        { title: 'Account Name', accessor: 'name' },
        { title: 'Account ID', accessor: 'id' },
        { title: 'Account Address', accessor: 'address' },
      ];
    }
    if (!kitId) {
      return [
        { title: '', accessor: 'select' },
        { title: 'Account Name', accessor: 'name' },
        { title: 'Account ID', accessor: 'id' },
        { title: 'Account Address', accessor: 'address' },
        { title: 'Coin Balance', accessor: 'coinBalance' },
      ];
    }
    return [
      { title: '', accessor: 'select' },
      { title: 'Account Name', accessor: 'name' },
      { title: 'Account ID', accessor: 'id' },
      { title: 'Account Address', accessor: 'address' },
      { title: 'Coin Balance', accessor: 'coinBalance' },
      { title: 'Token Balance', accessor: 'tokenBalance' },
    ];
  }, [kitId, chainId]);
  const ACCOUNT_RENDERER = useCallback(
    (data: Account) => {
      const isKitOwnerAccount = data.address === kitOwnerAddress;
      return {
        select: <RadioButton readOnly checked={selectedAccount?.account_id === data.account_id} onClick={() => setSelectedAccount(data)} />,
        name: (
          <div
            style={{
              display: 'flex',
              gap: 10,
              paddingRight: 10,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div
              title={data.account_id}
              style={{
                maxWidth: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {data.name}
            </div>
            {isKitOwnerAccount && <OwnerBadge />}
          </div>
        ),
        id: `${data.account_id.slice(0, 5)}...${data.account_id.slice(-5)}`,
        address: `${data.address.slice(0, 5)}...${data.address.slice(-5)}`,
        coinBalance: data.coin_balance === undefined ? '-' : data.coin_balance,
        tokenBalance: data.token_balance === undefined ? '-' : data.token_balance,
      };
    },
    [kitOwnerAddress, selectedAccount]
  );
  return (
    <div style={{ width: 810 }}>
      <CreateAccountModal
        isOpen={isOpenRegisterAccountModal}
        closeModal={() => setIsOpenRegisterAccountModal(false)}
        refreshAccounts={() => setRefresh(!refresh)}
      />
      <div style={{ marginBottom: 50 }}>
        {loading ? (
          <PageLoading style={{ height: 326 }} />
        ) : (
          <>
            {accounts.length === 0 ? (
              <EmptyDataTemplate>
                <div style={{ fontSize: 18, fontWeight: 500, lineHeight: '21px', color: '#7D828D' }}>There are no registered accounts yet.</div>
              </EmptyDataTemplate>
            ) : (
              <>
                <div className="kit-label" style={{ marginBottom: 20 }}>
                  Account List
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                  }}
                >
                  <BasicTable
                    data={accounts}
                    colGroup={ACCOUNT_COLGROUP}
                    headers={ACCOUNT_HEADERS}
                    renderer={ACCOUNT_RENDERER}
                    onClick={(account: Account) => setSelectedAccount(account)}
                  />
                  <PagingBar maxPageNum={maxPageNum} query={'select-account'} />
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        {setStep ? (
          <BasicButton theme={ButtonTheme.NORMAL} onClick={() => setStep(SELECT_ACCOUNT_STEP.SELECT_ACCOUNT_TYPE)}>
            Back
          </BasicButton>
        ) : (
          <BasicButton theme={ButtonTheme.NORMAL} onClick={() => onClosed()}>
            Close
          </BasicButton>
        )}
        <BasicButton
          onClick={() => {
            if (!selectedAccount) {
              return;
            }
            setSelectedAccount(undefined);
            onClickSelect(selectedAccount);
            onClosed();
          }}
          disabled={!selectedAccount}
        >
          Select
        </BasicButton>
      </div>
    </div>
  );
};

export default SelectAccount;
